/**
 * Pick — the ONE overlay picker (place, language, persona).
 *
 * Rendered outside the popup box (full-stage overlay). Click outside or Esc
 * closes. Multi-select for the opened prompt's places (lot E) and languages (lot D).
 *
 * While PROMPT_ZONE_EDITING is false: the place picker lists Location-derived
 * zones + Mondial only (no autocomplete, no "Autres lieux").
 * When true: text of ≥ 4 chars matching no listed place searches
 * GET /locations/autocomplete (« Autres lieux », ≤ 4 results → onPickZone).
 *
 * @see B-popup-frontend.md Files and props § Pick
 */
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, TextField, Tooltip } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import type { TargetZone, BrandPersona, GeoSuggestion } from '@traaker/shared';
import { TARGET_LIMITS, promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import { api } from '../../services/api';
import { countryFlag } from '../common/ZoneTypeIcon';
import { ZoneTypeIcon } from '../common/ZoneTypeIcon';
import type { LocKey } from './promptStudioModel';
import { PROMPT_ZONE_EDITING, MONDIAL, suggestionZones } from './promptStudioModel';

const MIN_SEARCH = TARGET_LIMITS.geoAutocompleteMinLength;

export type PickKind = 'place' | 'language' | 'persona' | 'term';

export interface PlaceOption {
  key: LocKey;
  zone: TargetZone | null;  // null = Mondial
  label: string;
}

export interface LanguageOption {
  code: string;
  label: string;
  measured?: boolean;
  /** Rendered left of the label (lot I3: the language flag). */
  icon?: React.ReactNode;
}

export interface PickProps {
  open: boolean;
  kind: PickKind;
  multi?: boolean;
  /** For place: selected LocKey(s). For language: selected code(s). For persona: selected id. */
  selected: string | string[];
  locked?: string[];
  onToggle: (value: string) => void;
  onClose: () => void;
  // Data props
  placeOptions?: PlaceOption[];   // for place picker
  languageOptions?: LanguageOption[];  // for language picker (lot D fills)
  personas?: BrandPersona[];  // for persona picker
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  /** Place search language: the brand working language, never the UI language. */
  lang?: string;
  /** Place picker: a search result was picked (it has no entry in placeOptions). No search without it. */
  onPickZone?: (zone: TargetZone) => void;
  /** Language picker: rows rendered in place of `languageOptions` (lot D's LanguagePickRows). */
  children?: React.ReactNode;
  /** Place picker: the title of a locked row (lot E: the root's place). */
  lockedTitle?: (value: string) => string;
  /** Term picker (theme / use case / criterion, owner 2026-10-30): the brand's vocabulary. */
  termOptions?: string[];
  /** Term picker: the search field's placeholder (« Rechercher parmi 120 cas d’usage »). */
  searchPlaceholder?: string;
}

/** Accent- and case-insensitive « contains » (« reno » finds « Rénovation »). */
export function termMatches(term: string, q: string): boolean {
  const fold = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return fold(term).includes(fold(q.trim()));
}

type PlaceSearch = { status: 'loading' | 'done' | 'failed'; zones: TargetZone[] };

export function Pick({
  open,
  kind,
  multi = false,
  selected,
  locked = [],
  onToggle,
  onClose,
  placeOptions = [],
  languageOptions = [],
  personas = [],
  searchQuery = '',
  onSearchChange,
  lang = 'fr',
  onPickZone,
  children,
  lockedTitle,
  termOptions = [],
  searchPlaceholder,
}: PickProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchSeq = useRef(0);
  const [search, setSearch] = useState<PlaceSearch | null>(null);
  const [attribution, setAttribution] = useState('© Geoapify');

  const placeQ = searchQuery.trim();
  const placeQLower = placeQ.toLowerCase();
  const listedPlaces = placeOptions
    .filter((o) => o.key !== MONDIAL)
    .filter((o) => !placeQ || o.label.toLowerCase().includes(placeQLower));
  const canSearch = open && kind === 'place' && PROMPT_ZONE_EDITING && !!onPickZone
    && placeQ.length >= MIN_SEARCH
    && listedPlaces.length === 0 && !'mondial'.includes(placeQLower);

  useEffect(() => {
    const seq = ++searchSeq.current;
    if (!canSearch) {
      setSearch(null);
      return;
    }
    setSearch({ status: 'loading', zones: [] });
    const timer = setTimeout(() => {
      api.get<{ suggestions: GeoSuggestion[]; attribution: string }>(
        `/locations/autocomplete?q=${encodeURIComponent(placeQ)}&lang=${encodeURIComponent(lang)}`,
      ).then(
        (r) => {
          if (seq !== searchSeq.current) return;
          setSearch({ status: 'done', zones: suggestionZones(r.suggestions ?? []) });
          if (r.attribution) setAttribution(r.attribution);
        },
        () => {
          if (seq === searchSeq.current) setSearch({ status: 'failed', zones: [] });
        },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [canSearch, placeQ, lang]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const isSelected = (v: string) =>
    Array.isArray(selected) ? selected.includes(v) : selected === v;

  const isLocked = (v: string) => locked.includes(v);

  const zoneIcon = (z: TargetZone) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: 14 }}>
        {z.countryCode ? countryFlag(z.countryCode) : ''}
      </Box>
      <ZoneTypeIcon zone={z} size={14} />
    </Box>
  );

  return (
    <>
      {/* Full-stage overlay */}
      <Box
        ref={overlayRef}
        onClick={(e) => {
          if (e.target === overlayRef.current) onClose();
        }}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(11,11,15,.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)',
            width: 320,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              inputRef={inputRef}
              size="small"
              fullWidth
              placeholder={
                searchPlaceholder ??
                (kind === 'place' ? 'Rechercher un lieu' : // i18n: promptStudio.pick.searchPlace
                kind === 'language' ? 'Rechercher une langue' : // i18n: promptStudio.pick.searchLanguage
                'Rechercher un persona') // i18n: promptStudio.pick.searchPersona
              }
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
            {kind === 'place' && PROMPT_ZONE_EDITING && placeQ.length >= 1 && placeQ.length < MIN_SEARCH && (
              <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5, px: 0.5 }}>
                {/* i18n: promptStudio.pick.minChars */}
                4 caractères au minimum pour chercher un autre lieu
              </Box>
            )}
          </Box>

          {/* List */}
          <Box sx={{ maxHeight: 330, overflowY: 'auto' }}>
            {kind === 'place' && (
              <>
                {/* Mondial first */}
                <PickRow
                  value={MONDIAL}
                  label="Mondial"
                  icon={<PublicIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                  selected={isSelected(MONDIAL)}
                  locked={isLocked(MONDIAL)}
                  lockedTitle={lockedTitle?.(MONDIAL)}
                  multi={multi}
                  onToggle={onToggle}
                />
                {listedPlaces.map((o) => (
                  <PickRow
                    key={o.key}
                    value={o.key}
                    label={o.label}
                    icon={
                      o.zone ? zoneIcon(o.zone) : (
                        <PublicIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      )
                    }
                    selected={isSelected(o.key)}
                    locked={isLocked(o.key)}
                    lockedTitle={lockedTitle?.(o.key)}
                    multi={multi}
                    onToggle={onToggle}
                  />
                ))}
                {search && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, fontSize: 12, color: 'text.disabled' }}>
                      {/* i18n: promptStudio.pick.otherPlaces */}
                      Autres lieux
                      {search.status === 'loading' && <CircularProgress size={12} />}
                    </Box>
                    {search.status === 'failed' && (
                      <Box sx={{ px: 2, pb: 1, fontSize: 12, color: 'text.secondary' }}>
                        {/* i18n: promptStudio.pick.searchUnavailable */}
                        Recherche indisponible
                      </Box>
                    )}
                    {search.status === 'done' && search.zones.length === 0 && (
                      <Box sx={{ px: 2, pb: 1, fontSize: 12, color: 'text.secondary' }}>
                        {/* i18n: promptStudio.pick.noPlace */}
                        Aucun lieu ne correspond
                      </Box>
                    )}
                    {search.zones.map((z) => {
                      const k = promptPlaceKey(z);
                      return (
                        <PickRow
                          key={k}
                          value={k}
                          label={zoneDisplayLabel(z)}
                          hint={z.label}
                          icon={zoneIcon(z)}
                          selected={isSelected(k)}
                          locked={false}
                          multi={multi}
                          onToggle={() => onPickZone?.(z)}
                        />
                      );
                    })}
                  </>
                )}
                {/* Geoapify attribution */}
                <Box sx={{ px: 2, py: 0.75, fontSize: 10.5, color: 'text.disabled', borderTop: '1px solid', borderColor: 'divider' }}>
                  {attribution}
                </Box>
              </>
            )}

            {kind === 'language' && children}
            {kind === 'language' && !children && (
              <>
                {languageOptions
                  .filter((o) =>
                    !searchQuery ||
                    o.label.toLowerCase().includes(searchQuery.toLowerCase()),
                  )
                  .map((o) => (
                    <PickRow
                      key={o.code}
                      value={o.code}
                      label={o.label}
                      icon={o.icon}
                      selected={isSelected(o.code)}
                      locked={isLocked(o.code)}
                      multi={multi}
                      onToggle={onToggle}
                    />
                  ))}
              </>
            )}

            {kind === 'term' && (() => {
              const shown = termOptions.filter((o) => !searchQuery.trim() || termMatches(o, searchQuery));
              return (
                <>
                  {/* Single choice: « Aucun » clears it (value '') */}
                  {!multi && (
                    <PickRow value="" label="Aucun" selected={isSelected('')} locked={false} multi={false} onToggle={onToggle} /> // i18n: promptStudio.pick.termNone
                  )}
                  {shown.map((o) => (
                    <PickRow key={o} value={o} label={o} selected={isSelected(o)} locked={false} multi={multi} onToggle={onToggle} />
                  ))}
                  {shown.length === 0 && (
                    <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
                      {/* i18n: promptStudio.pick.noTerm */}
                      {termOptions.length === 0 ? 'Rien dans Brand Knowledge' : 'Aucun élément ne correspond'}
                    </Box>
                  )}
                </>
              );
            })()}

            {kind === 'persona' && (
              <>
                {/* "No persona" option */}
                <PickRow
                  value=""
                  label="Aucun" // i18n: promptStudio.gen.personaNone
                  selected={isSelected('')}
                  locked={false}
                  multi={false}
                  onToggle={onToggle}
                />
                {personas
                  .filter((p) =>
                    !searchQuery ||
                    p.name.toLowerCase().includes(searchQuery.toLowerCase()),
                  )
                  .map((p) => (
                    <PickRow
                      key={p.brandPersonaId}
                      value={p.brandPersonaId}
                      label={p.name}
                      selected={isSelected(p.brandPersonaId)}
                      locked={false}
                      multi={false}
                      onToggle={onToggle}
                    />
                  ))}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

interface PickRowProps {
  value: string;
  label: string;
  /** Native title, e.g. a search result's full label (two « Saint-Denis » differ there). */
  hint?: string;
  icon?: React.ReactNode;
  selected: boolean;
  locked: boolean;
  /** Title of a locked row; the language picker's default when absent. */
  lockedTitle?: string;
  multi: boolean;
  onToggle: (value: string) => void;
}

function PickRow({ value, label, hint, icon, selected, locked, lockedTitle, multi, onToggle }: PickRowProps) {
  return (
    <Tooltip title={locked ? lockedTitle ?? 'Langue source, non modifiable' : ''} arrow placement="left">
      <Box
        component="button"
        title={hint}
        onClick={() => !locked && onToggle(value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 2,
          py: 1,
          fontSize: 13,
          textAlign: 'left',
          bgcolor: selected ? 'rgba(124,58,237,.06)' : 'transparent',
          border: 'none',
          cursor: locked ? 'default' : 'pointer',
          opacity: locked ? 0.5 : 1,
          '&:hover': !locked ? { bgcolor: 'rgba(0,0,0,.04)' } : {},
        }}
      >
        {icon && <Box sx={{ flexShrink: 0 }}>{icon}</Box>}
        <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
        {multi && selected && (
          <Box sx={{ fontSize: 16, color: '#7c3aed' }}>✓</Box>
        )}
        {!multi && selected && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed', flexShrink: 0 }} />
        )}
      </Box>
    </Tooltip>
  );
}
